// Zustand store для документа Кубикон CAD.
// Хранит:
//   - meta (title, id, isModified)
//   - parameters (пользовательские параметры с выражениями)
//   - features (timeline операций — Sketch, Extrude, ...)
//   - bodies, sketches (метаданные)
//   - selection (что выбрано: тело/грань/ребро/вершина)
//   - mode (3D-вид / редактирование эскиза)
//   - kernelStatus (статус загрузки WASM-ядра)
//
// Главное правило: в store НЕ хранятся B-rep тела (это TopoDS_Shape в C++ heap).
// Они живут в Worker'е. В store — только meta + ссылки + триангуляция-меши для
// рендера (Float32Array вершин, Uint32Array индексов).

import { create } from 'zustand';


const initialDocument = () => ({
    schemaVersion: 1,
    title: 'Без названия',
    parameters: [],
    features: [],
    bodies: [],
    sketches: [],
    // Рабочие плоскости со смещением — { id, basePlane, offset, name }.
    // Не строят геометрию; на них можно создавать эскизы.
    workplanes: [],
    // Рабочие оси — { id, p1:[x,y,z], p2:[x,y,z], name }. Произвольная
    // ось через 2 точки; используется как ось кругового массива.
    workaxes: [],
    /*
     * Механические соединения между ТЕЛАМИ.
     *
     * Полноценных компонентов (как в Fusion) у нас нет и пока не планируется:
     * для робототехники достаточно связать два тела и проверить, как механизм
     * двигается. Компонент со своей историей здесь чаще мешает, чем помогает.
     *
     * { id, kind, bodyA, bodyB, origin:[x,y,z], axis:[x,y,z],
     *   value, min, max, name }
     *
     *   kind   — 'rigid' | 'revolute' | 'slider'
     *   bodyA  — неподвижное тело (основание)
     *   bodyB  — подвижное
     *   origin — точка соединения в мировых координатах
     *   axis   — ось вращения (revolute) или направление хода (slider)
     *   value  — текущее положение: градусы или мм
     *   min/max — пределы хода; null — без ограничения
     */
    joints: [],
});


// === История (Undo/Redo) ===
// Снимок состояния документа: всё что можно откатить.
// bodyMeshes/selection/mode сюда НЕ кладём (это transient: тела пересоздаются
// rebuildAllBodies'ом, выбор и режим — текущая работа юзера).
const MAX_HISTORY = 50;

function snapshotDoc(state) {
    return {
        doc: {
            schemaVersion: state.doc.schemaVersion,
            title:        state.doc.title,
            parameters:   JSON.parse(JSON.stringify(state.doc.parameters || [])),
            features:     JSON.parse(JSON.stringify(state.doc.features   || [])),
            bodies:       JSON.parse(JSON.stringify(state.doc.bodies     || [])),
            sketches:     JSON.parse(JSON.stringify(state.doc.sketches   || [])),
            workplanes:   JSON.parse(JSON.stringify(state.doc.workplanes || [])),
            workaxes:     JSON.parse(JSON.stringify(state.doc.workaxes   || [])),
            joints:       JSON.parse(JSON.stringify(state.doc.joints     || [])),
        },
        bodyVisibility:   [...(state.bodyVisibility   || new Map()).entries()],
        sketchVisibility: [...(state.sketchVisibility || new Map()).entries()],
        cloudProjectId:   state.cloudProjectId,
        isModified:       state.isModified,
    };
}

function applySnapshot(snap) {
    return {
        doc: snap.doc,
        bodyVisibility:   new Map(snap.bodyVisibility),
        sketchVisibility: new Map(snap.sketchVisibility),
        cloudProjectId:   snap.cloudProjectId,
        isModified:       snap.isModified,
        // Сбрасываем transient-поля чтобы UI не показывал «зависшие» меню/выбор
        // от старого состояния
        selection:        { kind: null, ids: [] },
        pendingFeature:   null,
        pendingShape:     null,
        bodyContextMenu:  null,
    };
}


const useCadStore = create((set, get) => ({
    // === Документ ===
    doc: initialDocument(),
    isModified: false,
    /** id проекта в облаке (null если документ ещё не сохранён в облаке).
     *  Если задан — «Сохранить» будет PUT, иначе POST. */
    cloudProjectId: null,
    setCloudProjectId: (id) => set({ cloudProjectId: id }),
    /** Открыто ли модальное окно «Мои проекты». Управляется TopBar. */
    cloudModalOpen: false,
    setCloudModalOpen: (v) => set({ cloudModalOpen: !!v }),
    /** Открыто ли модальное окно «Сохранить в облако» (title/description/public). */
    cloudSaveModalOpen: false,
    setCloudSaveModalOpen: (v) => set({ cloudSaveModalOpen: !!v }),

    // === Undo / Redo ===
    // Снимки доступные для отмены (past) и для повтора (future).
    // Каждое значимое изменение doc/visibility должно вызвать pushHistory()
    // ДО мутации. Все mutating-actions ниже делают это автоматически.
    historyPast:   [],
    historyFuture: [],

    /** Сохранить текущее состояние в past. Future чистится (новая «ветка»). */
    pushHistory: () =>
        set((s) => {
            const snap = snapshotDoc(s);
            const next = [...s.historyPast, snap];
            if (next.length > MAX_HISTORY) next.shift();
            return { historyPast: next, historyFuture: [] };
        }),

    /** Откатить последнее изменение. После undo нужно вызвать rebuildAllBodies()
     *  снаружи, чтобы тела в worker'е соответствовали восстановленным фичам. */
    undo: () => {
        const s = useCadStore.getState();
        if (s.historyPast.length === 0) return false;
        const past = [...s.historyPast];
        const snap = past.pop();
        const future = [...s.historyFuture, snapshotDoc(s)];
        if (future.length > MAX_HISTORY) future.shift();
        set({ ...applySnapshot(snap), historyPast: past, historyFuture: future });
        return true;
    },

    redo: () => {
        const s = useCadStore.getState();
        if (s.historyFuture.length === 0) return false;
        const future = [...s.historyFuture];
        const snap = future.pop();
        const past = [...s.historyPast, snapshotDoc(s)];
        if (past.length > MAX_HISTORY) past.shift();
        set({ ...applySnapshot(snap), historyPast: past, historyFuture: future });
        return true;
    },

    canUndo: () => useCadStore.getState().historyPast.length > 0,
    canRedo: () => useCadStore.getState().historyFuture.length > 0,

    /** Внутренний helper: атомарно сохранить snapshot + применить мутацию.
     *  mutator — функция (state) => partialNewState.
     *  Используется во всех action'ах ниже, чтобы Undo откатывал всё цельно. */
    _mutateWithHistory: (mutator) =>
        set((s) => {
            const snap = snapshotDoc(s);
            const past = [...s.historyPast, snap];
            if (past.length > MAX_HISTORY) past.shift();
            return {
                ...mutator(s),
                historyPast: past,
                historyFuture: [],
            };
        }),

    // === Состояние ядра ===
    kernelStatus: 'idle', // 'idle' | 'loading' | 'ready' | 'error'
    kernelError: null,

    // === Меши для рендера (заполняются после операций ядра) ===
    // Map<bodyId, { positions: Float32Array, indices: Uint32Array, normals: Float32Array, edges: Float32Array }>
    bodyMeshes: new Map(),

    // === Режим работы ===
    // 'view3d'        — обычный 3D-вид
    // 'select_plane'  — выбор плоскости для нового эскиза (показываем XY/XZ/YZ + грани)
    // 'sketch'        — рисование на выбранной плоскости (камера развёрнута перпендикулярно)
    // 'feature_edit'  — редактирование параметров существующей операции
    mode: 'view3d',
    activeSketchId: null,
    activeFeatureId: null,
    // Hover-плоскость в режиме select_plane
    hoveredPlane: null, // null | 'XY' | 'XZ' | 'YZ'
    setHoveredPlane: (plane) => set({ hoveredPlane: plane }),

    // Контекстное меню тела в 3D-сцене (правый клик по BodyMesh).
    // null | { bodyId, x, y } — DOM-координаты для overlay-меню.
    bodyContextMenu: null,
    openBodyContextMenu: (bodyId, x, y) =>
        set({ bodyContextMenu: { bodyId, x, y } }),
    closeBodyContextMenu: () => set({ bodyContextMenu: null }),

    // Контекстное меню профиля эскиза (клик по замкнутой области в 3D).
    // { profileRefs: ['sketchId/profileId', ...], x, y } — может быть несколько.
    profileContextMenu: null,
    openProfileContextMenu: (profileRefs, x, y) => {
        const refs = Array.isArray(profileRefs) ? profileRefs : [profileRefs];
        set({ profileContextMenu: { profileRefs: refs, x, y } });
    },
    closeProfileContextMenu: () => set({ profileContextMenu: null }),

    // === Выделение ===
    selection: {
        // null | 'body' | 'face' | 'edge' | 'vertex' | 'sketch_primitive' | 'profile'
        // 'profile' — выбран замкнутый контур эскиза (для extrude)
        kind: null,
        ids: [],       // массив id (для 'profile' — sketchId+'/'+profileId)
    },

    // === Активная операция (Extrude / Revolve / ...) ===
    // null | { kind: 'extrude', profileRefs: [...], distance, direction, operation }
    pendingFeature: null,
    setPendingFeature: (f) => {
        // Старт ДРУГОЙ операции поверх незафиксированного примитива — фиксируем
        // примитив как feature (иначе его тело потеряется при перезагрузке).
        // Не трогаем при отмене (f=null) и при смене типа примитива (f.kind===
        // 'primitive'), чтобы не плодить дубли.
        if (f && f.kind !== 'primitive') {
            get().commitPendingPrimitive();
        }
        // Запоминаем последнюю запущенную операцию — для «Повторить»
        // в радиальном меню (верхний сектор, как в Fusion).
        if (f && f.kind) set({ lastCommandKind: f.kind });
        set({ pendingFeature: f });
    },

    /*
     * Последняя команда и её повтор.
     *
     * В Fusion «Повторить» всегда висит вверху радиального меню, и это одна
     * из самых частых кнопок: поставил скругление на одном ребре — правый
     * клик, вверх, ставишь на следующем, не возвращаясь в ленту.
     */
    lastCommandKind: null,
    repeatLastCommand: () => {
        const kind = get().lastCommandKind;
        if (!kind) return;
        const defaults = {
            fillet:   { kind: 'fillet',  radius: 2, edgeRefs: [] },
            chamfer:  { kind: 'chamfer', distance: 2, edgeRefs: [] },
            shell:    { kind: 'shell',   thickness: 2, faceRefs: [] },
            extrude:  { kind: 'extrude', distance: 20, direction: 'one', operation: 'new' },
            revolve:  { kind: 'revolve', axis: 'sketch_Y', angle: 360, operation: 'new' },
        };
        const f = defaults[kind];
        if (f) set({ pendingFeature: { ...f } });
    },
    updatePendingFeature: (updates) => set((s) => s.pendingFeature
        ? { pendingFeature: { ...s.pendingFeature, ...updates } } : {}),

    // === Камера / вид ===
    cameraView: 'iso', // 'iso' | 'top' | 'front' | 'right' | 'left' | 'back' | 'bottom'

    // === UI-флаги ===
    ui: {
        browserVisible: true,
        timelineVisible: true,
        propertiesVisible: false,
        gridVisible: true,
        originVisible: true,
    },

    // === Действия ===

    setKernelStatus: (status, error = null) =>
        set({ kernelStatus: status, kernelError: error }),

    setMode: (mode) => set({ mode }),

    setSelection: (kind, ids) => set({ selection: { kind, ids } }),
    clearSelection: () => set({ selection: { kind: null, ids: [] }, selectedFaceData: null }),

    // Дополнительные данные о выбранной грани (когда selection.kind === 'face').
    // Содержит planeRef + контур грани в 3D (Vector3 array). Нужно для extrude
    // грани тела — иначе пришлось бы пересчитывать каждый раз через FacePicker.
    selectedFaceData: null,
    setSelectedFaceData: (data) => set({ selectedFaceData: data }),

    setCameraView: (view) => set({ cameraView: view }),

    toggleUiPanel: (key) =>
        set((s) => ({ ui: { ...s.ui, [key]: !s.ui[key] } })),

    // Документ-операции
    setDocTitle: (title) =>
        get()._mutateWithHistory((s) => ({
            doc: { ...s.doc, title },
            isModified: true,
        })),

    addFeature: (feature) =>
        get()._mutateWithHistory((s) => ({
            doc: { ...s.doc, features: [...s.doc.features, feature] },
            isModified: true,
        })),

    updateFeature: (id, updates) =>
        get()._mutateWithHistory((s) => ({
            doc: {
                ...s.doc,
                features: s.doc.features.map((f) =>
                    f.id === id ? { ...f, ...updates } : f
                ),
            },
            isModified: true,
        })),

    removeFeature: (id) =>
        get()._mutateWithHistory((s) => ({
            doc: { ...s.doc, features: s.doc.features.filter((f) => f.id !== id) },
            isModified: true,
        })),

    // Переставить фичу на одну позицию (dir: -1 влево / +1 вправо).
    // Проверку зависимостей делает вызывающий (Timeline) — стор просто меняет
    // порядок. Возвращать ничего не нужно (мутация через историю).
    moveFeatureBy: (id, dir) =>
        get()._mutateWithHistory((s) => {
            const feats = [...s.doc.features];
            const i = feats.findIndex((f) => f.id === id);
            if (i < 0) return {};
            const j = i + dir;
            if (j < 0 || j >= feats.length) return {};
            [feats[i], feats[j]] = [feats[j], feats[i]];
            return { doc: { ...s.doc, features: feats }, isModified: true };
        }),

    // Rollback-маркер: до какого шага (индекс, эксклюзивно) строить модель.
    // null = строить всё. Фичи с индексом >= rollbackIndex пропускаются при
    // rebuild — это «перемотка истории» как в Fusion (можно вставить операцию
    // в середину или посмотреть модель на раннем шаге). НЕ пишется в историю
    // undo — это состояние просмотра, не изменение документа.
    rollbackIndex: null,
    setRollbackIndex: (idx) => set({ rollbackIndex: idx }),

    // Подавить/включить фичу (suppress). Подавленная фича пропускается при
    // rebuild (как в Fusion) — но остаётся в хронологии и легко включается.
    setFeatureSuppressed: (id, suppressed) =>
        get()._mutateWithHistory((s) => ({
            doc: {
                ...s.doc,
                features: s.doc.features.map((f) =>
                    f.id === id ? { ...f, suppressed: !!suppressed } : f
                ),
            },
            isModified: true,
        })),

    // === Тосты (неблокирующие уведомления вместо alert) ===
    // toast: { id, text, type: 'info'|'error'|'success' }
    toasts: [],
    pushToast: (text, type = 'info') => {
        /*
         * ПОВТОРЫ СХЛОПЫВАЕМ, А НЕ КОПИМ.
         *
         * Ошибка часто приходит не по одному разу: пользователь кликает
         * несколько раз подряд, либо одно действие задевает сразу несколько
         * элементов. Раньше каждый вызов добавлял отдельный тост, и за 6 секунд
         * жизни ошибки экран забивался десятком одинаковых плашек — они
         * перекрывали панель урока и саму сцену.
         *
         * Теперь такой же текст, уже висящий на экране, не дублируется:
         * показываем счётчик повторов и продлеваем время жизни.
         */
        // Авто-скрытие через 4с (ошибки держим дольше — 6с).
        const ttl = type === 'error' ? 6000 : 4000;
        // Таймеры держим отдельно от состояния: их нельзя класть в стор,
        // иначе они попадут в снапшоты истории и сериализацию документа.
        const timers = useCadStore._toastTimers || (useCadStore._toastTimers = new Map());
        const scheduleHide = (tid) => {
            clearTimeout(timers.get(tid));
            timers.set(tid, setTimeout(() => {
                timers.delete(tid);
                set((s) => ({ toasts: s.toasts.filter((t) => t.id !== tid) }));
            }, ttl));
        };

        const existing = get().toasts.find((t) => t.text === text && t.type === type);
        if (existing) {
            set((s) => ({
                toasts: s.toasts.map((t) => (
                    t.id === existing.id ? { ...t, count: (t.count || 1) + 1 } : t
                )),
            }));
            // Отсчёт начинаем заново — иначе повторная ошибка исчезла бы
            // по таймеру самого первого показа, иногда почти сразу.
            scheduleHide(existing.id);
            return existing.id;
        }
        const id = `toast_${Date.now().toString(36)}_${Math.floor(performance.now())}`;
        set((s) => ({ toasts: [...s.toasts, { id, text, type }] }));
        scheduleHide(id);
        return id;
    },
    dismissToast: (id) => {
        // Снимаем и таймер: иначе он сработает вхолостую, а если за это время
        // появится тост с тем же id — погасит уже чужой.
        const timers = useCadStore._toastTimers;
        if (timers) { clearTimeout(timers.get(id)); timers.delete(id); }
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    },

    // === Пользовательские параметры (уравнения) ===
    // Параметр: { id, name, expression, unit, comment }.
    parametersModalOpen: false,
    setParametersModalOpen: (open) => set({ parametersModalOpen: !!open }),
    measureModalOpen: false,
    setMeasureModalOpen: (open) => set({ measureModalOpen: !!open }),
    addParameter: (param) =>
        get()._mutateWithHistory((s) => ({
            doc: { ...s.doc, parameters: [...(s.doc.parameters || []), param] },
            isModified: true,
        })),
    updateParameter: (id, updates) =>
        get()._mutateWithHistory((s) => ({
            doc: {
                ...s.doc,
                parameters: (s.doc.parameters || []).map((p) =>
                    p.id === id ? { ...p, ...updates } : p
                ),
            },
            isModified: true,
        })),
    removeParameter: (id) =>
        get()._mutateWithHistory((s) => ({
            doc: {
                ...s.doc,
                parameters: (s.doc.parameters || []).filter((p) => p.id !== id),
            },
            isModified: true,
        })),

    // === Рабочие плоскости ===
    addWorkplane: (wp) =>
        get()._mutateWithHistory((s) => ({
            doc: { ...s.doc, workplanes: [...(s.doc.workplanes || []), wp] },
            isModified: true,
        })),

    updateWorkplane: (id, updates) =>
        get()._mutateWithHistory((s) => ({
            doc: {
                ...s.doc,
                workplanes: (s.doc.workplanes || []).map((w) =>
                    w.id === id ? { ...w, ...updates } : w
                ),
            },
            isModified: true,
        })),

    removeWorkplane: (id) =>
        get()._mutateWithHistory((s) => ({
            doc: {
                ...s.doc,
                workplanes: (s.doc.workplanes || []).filter((w) => w.id !== id),
            },
            isModified: true,
        })),

    // === Рабочие оси ===
    addWorkaxis: (ax) =>
        get()._mutateWithHistory((s) => ({
            doc: { ...s.doc, workaxes: [...(s.doc.workaxes || []), ax] },
            isModified: true,
        })),

    removeWorkaxis: (id) =>
        get()._mutateWithHistory((s) => ({
            doc: {
                ...s.doc,
                workaxes: (s.doc.workaxes || []).filter((a) => a.id !== id),
            },
            isModified: true,
        })),

    // === Механические соединения между телами ===
    addJoint: (j) =>
        get()._mutateWithHistory((s) => ({
            doc: { ...s.doc, joints: [...(s.doc.joints || []), j] },
            isModified: true,
        })),

    removeJoint: (id) =>
        get()._mutateWithHistory((s) => ({
            doc: {
                ...s.doc,
                joints: (s.doc.joints || []).filter((j) => j.id !== id),
            },
            isModified: true,
        })),

    /*
     * Изменение положения соединения — БЕЗ записи в историю.
     *
     * Человек тянет ползунок, проверяя ход механизма, и делает это десятки
     * раз подряд. Каждый шаг в истории забил бы её целиком и сделал Ctrl+Z
     * бесполезным: чтобы отменить предыдущую операцию, пришлось бы нажать
     * его сотню раз.
     */
    setJointValue: (id, value) =>
        set((s) => ({
            doc: {
                ...s.doc,
                joints: (s.doc.joints || []).map(
                    (j) => (j.id === id ? { ...j, value } : j),
                ),
            },
        })),

    // Тела (метаданные)
    setBodies: (bodies) =>
        set((s) => ({ doc: { ...s.doc, bodies } })),

    setBodyMesh: (bodyId, mesh) =>
        set((s) => {
            const m = new Map(s.bodyMeshes);
            m.set(bodyId, mesh);
            return { bodyMeshes: m };
        }),

    clearBodyMeshes: () => set({ bodyMeshes: new Map() }),

    // === Видимость тел и эскизов (Browser-tree «глаз») ===
    // Если bodyId/sketchId нет в Map — считаем видимым (default: true).
    bodyVisibility: new Map(),
    sketchVisibility: new Map(),

    toggleBodyVisible: (bodyId) =>
        get()._mutateWithHistory((s) => {
            const m = new Map(s.bodyVisibility);
            const cur = m.has(bodyId) ? m.get(bodyId) : true;
            m.set(bodyId, !cur);
            return { bodyVisibility: m };
        }),

    setBodyVisible: (bodyId, visible) =>
        get()._mutateWithHistory((s) => {
            const m = new Map(s.bodyVisibility);
            m.set(bodyId, !!visible);
            return { bodyVisibility: m };
        }),

    toggleSketchVisible: (sketchId) =>
        get()._mutateWithHistory((s) => {
            const m = new Map(s.sketchVisibility);
            const cur = m.has(sketchId) ? m.get(sketchId) : true;
            m.set(sketchId, !cur);
            return { sketchVisibility: m };
        }),

    setSketchVisible: (sketchId, visible) =>
        get()._mutateWithHistory((s) => {
            const m = new Map(s.sketchVisibility);
            m.set(sketchId, !!visible);
            return { sketchVisibility: m };
        }),

    // Удалить тело целиком (mesh + visibility-флаг). Worker уведомляется
    // отдельно через geometryClient.deleteBody, см. там.
    removeBody: (bodyId) =>
        get()._mutateWithHistory((s) => {
            const m = new Map(s.bodyMeshes);
            m.delete(bodyId);
            const v = new Map(s.bodyVisibility);
            v.delete(bodyId);
            // Снимаем выделение если удаляемое тело было выделено
            const sel = s.selection;
            const newSel = (sel.kind === 'body' && sel.ids.includes(bodyId))
                ? { kind: null, ids: [] }
                : sel;
            return { bodyMeshes: m, bodyVisibility: v, selection: newSel };
        }),

    // Эскизы
    addSketch: (sketch) =>
        get()._mutateWithHistory((s) => ({
            doc: { ...s.doc, sketches: [...s.doc.sketches, sketch] },
            isModified: true,
        })),

    updateSketch: (id, updates) =>
        get()._mutateWithHistory((s) => ({
            doc: {
                ...s.doc,
                sketches: s.doc.sketches.map((sk) =>
                    sk.id === id ? { ...sk, ...updates } : sk
                ),
            },
            isModified: true,
        })),

    // Запросить выбор плоскости (новый эскиз)
    // Зафиксировать НЕЗАВЕРШЁННЫЙ примитив как feature. Тело примитива
    // создаётся как live-preview СРАЗУ (в bodyMeshes), но feature добавляется
    // только по кнопке OK. Если пользователь, не нажав OK, начинает следующую
    // операцию (например, эскиз на грани этого тела) — без фиксации тело живёт
    // только в текущей сессии и ПРОПАДАЕТ после перезагрузки (rebuild строит
    // документ только из features). Поэтому перед началом новой операции
    // фиксируем висящий примитив (как во Fusion — активная операция
    // завершается при старте следующей). Возвращает true, если что-то зафиксировал.
    commitPendingPrimitive: () => {
        const s = get();
        const pf = s.pendingFeature;
        if (!pf || pf.kind !== 'primitive') return false;
        if (pf.editingFeatureId) return false; // редактирование — не создание
        // Тело должно реально существовать в сцене.
        if (!pf.bodyId || !s.bodyMeshes.has(pf.bodyId)) return false;
        const feature = {
            id: `feat_${Date.now().toString(36)}`,
            kind: 'primitive',
            primitiveKind: pf.primitiveKind,
            position: pf.position || [0, 0, 0],
            width: pf.width, depth: pf.depth, height: pf.height,
            radius: pf.radius, tube: pf.tube,
            createdAt: Date.now(),
        };
        get().addFeature(feature);
        set({ pendingFeature: null });
        return true;
    },

    enterSelectPlaneMode: () => {
        // Сначала фиксируем незавершённый примитив (иначе тело потеряется при
        // перезагрузке — см. commitPendingPrimitive).
        get().commitPendingPrimitive();
        set({ mode: 'select_plane', hoveredPlane: null });
    },

    // Выйти из режима выбора плоскости (отмена)
    cancelSelectPlane: () =>
        set({ mode: 'view3d', hoveredPlane: null }),

    enterSketchMode: (sketchId) =>
        set({ mode: 'sketch', activeSketchId: sketchId, hoveredPlane: null,
              sketchSolveStatus: null }),

    exitSketchMode: () =>
        set({ mode: 'view3d', activeSketchId: null, activeSketchTool: null,
              sketchSolveStatus: null }),

    // Статус решателя активного эскиза для индикации определённости:
    //   { dof: number|null, conflicts: string[], failed: bool } | null
    //   dof===0 → полностью определён; dof>0 → недоопределён; conflicts.length → конфликт.
    sketchSolveStatus: null,
    setSketchSolveStatus: (st) => set({ sketchSolveStatus: st }),

    // Активный инструмент рисования эскиза: 'line' | 'rectangle' | 'circle' | 'arc' | 'polygon' | 'point' | null
    activeSketchTool: null,
    setActiveSketchTool: (tool) => set({ activeSketchTool: tool }),

    /* Черновик текста для эскиза: что писать и каким размером.
       Заполняется панелью над сценой, применяется кликом по плоскости —
       так человек видит, КУДА встанет надпись (как в 123D). */
    textDraft: null,     // null | { text, fontSize }
    setTextDraft: (d) => set({ textDraft: d }),

    // Число сторон правильного многоугольника (инструмент 'polygon').
    // 3..64. Управляется панелью ввода при активном инструменте polygon.
    polygonSides: 6,
    setPolygonSides: (n) =>
        set({ polygonSides: Math.max(3, Math.min(64, Math.round(n) || 6)) }),

    // Расстояние offset для инструмента «Смещение» в эскизе (>0 наружу, <0 внутрь).
    offsetDistance: -5,
    setOffsetDistance: (v) => set({ offsetDistance: Number(v) || 0 }),

    // Радиус скругления углов в эскизе. Держится между кликами: скругляют
    // обычно все углы контура одним радиусом, вводить его каждый раз заново
    // было бы утомительно.
    /*
     * Параметры зеркала и массива в эскизе. Держатся между вызовами:
     * массив отверстий обычно задают одним шагом на весь ряд.
     */
    sketchMirrorAxis: 'y',          // 'x' | 'y'
    setSketchMirrorAxis: (v) => set({ sketchMirrorAxis: v === 'x' ? 'x' : 'y' }),
    sketchPatternCount: 3,
    setSketchPatternCount: (v) => set({
        sketchPatternCount: Math.max(2, Math.floor(Number(v) || 3)),
    }),
    sketchPatternDx: 20,
    setSketchPatternDx: (v) => set({ sketchPatternDx: Number(v) || 0 }),
    sketchPatternDy: 0,
    setSketchPatternDy: (v) => set({ sketchPatternDy: Number(v) || 0 }),

    sketchFilletRadius: 3,
    setSketchFilletRadius: (v) => set({
        sketchFilletRadius: Math.max(0.1, Number(v) || 3),
    }),

    // Запрос применить геометрическое ограничение к выделенным
    // примитивам эскиза. TopBar ставит { kind, ts }, SketchOverlay
    // ловит через useEffect (там есть и selection, и решатель).
    // ts — timestamp, чтобы повторный клик той же кнопки сработал.
    requestConstraint: null,
    setRequestConstraint: (kind) =>
        set({ requestConstraint: kind ? { kind, ts: Date.now() } : null }),

    // Активный инструмент навигации в sketch-mode: 'pan' | 'orbit' | null.
    // Управляется SketchCameraToolbar. Когда активен — Viewport-overlay
    // перехватывает ЛКМ-drag и двигает/крутит камеру.
    cameraTool: null,
    setCameraTool: (tool) => set({ cameraTool: tool }),

    // pendingShape — фигура в процессе рисования.
    // Состояния: null | { tool, start: {x,y}, end: {x,y}, phase: 'drag'|'click'|'placed' }
    //   tool — 'line' | 'rectangle' | 'circle'
    //   phase 'drag'   — юзер зажал и тащит, end меняется по мыши
    //   phase 'click'  — была фаза drag, но drag был короткий → ждём второй клик
    //                    (либо юзер сразу кликнул один раз)
    //   phase 'placed' — drag завершён длинным движением, end зафиксирован,
    //                    ждём подтверждения галочкой / Enter
    pendingShape: null,
    setPendingShape: (s) => set({ pendingShape: s }),
    updatePendingShapeEnd: (end) => set((s) => s.pendingShape
        ? { pendingShape: { ...s.pendingShape, end } } : {}),
    updatePendingShapePhase: (phase) => set((s) => s.pendingShape
        ? { pendingShape: { ...s.pendingShape, phase } } : {}),

    // Зафиксированные значения size-инпутов (юзер нажал замочек).
    // null | { width?, height?, length?, radius? }
    drawSizeOverride: null,
    setDrawSizeOverride: (o) => set({ drawSizeOverride: o }),

    // Добавить примитив в активный эскиз
    addSketchPrimitive: (primitive) =>
        get()._mutateWithHistory((s) => {
            const sketches = s.doc.sketches.map((sk) =>
                sk.id === s.activeSketchId
                    ? { ...sk, primitives: [...(sk.primitives || []), primitive] }
                    : sk
            );
            return { doc: { ...s.doc, sketches }, isModified: true };
        }),

    // Обновить примитив в активном эскизе.
    // ВАЖНО: НЕ оборачиваем в _mutateWithHistory — этот action вызывается
    // на каждый кадр drag'а точки эскиза (sketch solver). Иначе history
    // забьётся за секунду и Undo станет бесполезным. История делается на
    // целом drag'е через snapshot до начала и после finalize (TODO).
    updateSketchPrimitive: (primId, updates) =>
        set((s) => {
            const sketches = s.doc.sketches.map((sk) =>
                sk.id === s.activeSketchId
                    ? {
                        ...sk,
                        primitives: (sk.primitives || []).map((p) =>
                            p.id === primId ? { ...p, ...updates } : p
                        ),
                    }
                    : sk
            );
            return { doc: { ...s.doc, sketches }, isModified: true };
        }),

    // Добавить ограничение (constraint) в активный эскиз.
    // Хранятся в sketch.constraints. Размерные (distance) и
    // геометрические (parallel, tangent…) — единый список.
    addSketchConstraint: (constraint) =>
        get()._mutateWithHistory((s) => {
            const sketches = s.doc.sketches.map((sk) =>
                sk.id === s.activeSketchId
                    ? { ...sk, constraints: [...(sk.constraints || []), constraint] }
                    : sk
            );
            return { doc: { ...s.doc, sketches }, isModified: true };
        }),

    // Удалить ограничение из активного эскиза.
    removeSketchConstraint: (constraintId) =>
        get()._mutateWithHistory((s) => {
            const sketches = s.doc.sketches.map((sk) =>
                sk.id === s.activeSketchId
                    ? {
                        ...sk,
                        constraints: (sk.constraints || [])
                            .filter((c) => c.id !== constraintId),
                    }
                    : sk
            );
            return { doc: { ...s.doc, sketches }, isModified: true };
        }),

    // Обновить ограничение (например, изменить value размера).
    updateSketchConstraint: (constraintId, updates) =>
        get()._mutateWithHistory((s) => {
            const sketches = s.doc.sketches.map((sk) =>
                sk.id === s.activeSketchId
                    ? {
                        ...sk,
                        constraints: (sk.constraints || []).map((c) =>
                            c.id === constraintId ? { ...c, ...updates } : c
                        ),
                    }
                    : sk
            );
            return { doc: { ...s.doc, sketches }, isModified: true };
        }),

    // Удалить примитив
    removeSketchPrimitive: (primId) =>
        get()._mutateWithHistory((s) => {
            const sketches = s.doc.sketches.map((sk) => {
                if (sk.id !== s.activeSketchId) return sk;
                const prims = sk.primitives || [];
                const target = prims.find((p) => p.id === primId);
                if (!target) return sk;
                // Удаляем сам примитив.
                let left = prims.filter((p) => p.id !== primId);
                // Если это линия/дуга — подчищаем её концевые точки, если они
                // больше нигде не используются (осиротевшие точки эскиза).
                if (target.kind === 'line' || target.kind === 'arc') {
                    for (const ptId of [target.p1, target.p2]) {
                        const stillUsed = left.some((p) =>
                            (p.kind === 'line' || p.kind === 'arc') &&
                            (p.p1 === ptId || p.p2 === ptId));
                        if (!stillUsed) left = left.filter((p) => p.id !== ptId);
                    }
                }
                // Убираем ограничения, ссылающиеся на исчезнувшие примитивы/точки.
                const remaining = new Set(left.map((p) => p.id));
                const REF_KEYS = ['lineId', 'line1Id', 'line2Id', 'p1Id', 'p2Id',
                    'pointId', 'circleId', 'c1Id', 'c2Id', 'aId', 'bId', 'axisLineId'];
                const constraints = (sk.constraints || []).filter((c) =>
                    REF_KEYS.every((k) => c[k] == null || remaining.has(c[k]))
                );
                return { ...sk, primitives: left, constraints };
            });
            return { doc: { ...s.doc, sketches }, isModified: true };
        }),

    // Сброс документа. История ПОЛНОСТЬЮ очищается — Undo не должен
    // вернуть юзера к старому проекту после New / Load.
    newDocument: () =>
        set({
            doc: initialDocument(),
            isModified: false,
            bodyMeshes: new Map(),
            bodyVisibility: new Map(),
            sketchVisibility: new Map(),
            selection: { kind: null, ids: [] },
            mode: 'view3d',
            activeSketchId: null,
            activeFeatureId: null,
            cloudProjectId: null,
            historyPast: [],
            historyFuture: [],
        }),

    loadDocument: (newDoc) =>
        set({
            doc: { ...initialDocument(), ...newDoc },
            isModified: false,
            bodyMeshes: new Map(),
            bodyVisibility: new Map(),
            sketchVisibility: new Map(),
            selection: { kind: null, ids: [] },
            mode: 'view3d',
            activeSketchId: null,
            activeFeatureId: null,
            cloudProjectId: null,
            historyPast: [],
            historyFuture: [],
        }),

    markSaved: () => set({ isModified: false }),
}));

export default useCadStore;
